/**
 * Small, independently-testable view-logic units used by BookingPortal.tsx's
 * slot-button grids and the always-visible emergency-call line.
 *
 * Pulled out of the ~2600-line BookingPortal component so they can be unit
 * tested without rendering the full component / driving its "Find Slots"
 * availability flow.
 */
import React from 'react';
import { Typography } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;

export interface RolloverSlotLike {
  start_time: string;
  end_time?: string;
  is_multi_day?: boolean;
  segments?: Array<{ date: string; start: string; end: string; minutes: number }>;
}

/**
 * Formats the actual finish day+time for a multi-day (rollover) slot, e.g.
 * "Wed 2:00 PM". Prefers the last segment's end (the server-derived plan)
 * when present, falling back to the slot's overall end_time. Returns '' when
 * neither is available so callers can fall back to a generic label.
 */
export function rolloverFinishLabel(slot: RolloverSlotLike): string {
  const lastSeg = slot.segments && slot.segments.length ? slot.segments[slot.segments.length - 1] : null;
  const finish = lastSeg?.end || slot.end_time;
  if (!finish) return '';
  return dayjs(finish).format('ddd h:mm A');
}

/**
 * Format a slot's button label: the start time, plus " → {finish}" when the
 * slot is a multi-day rollover slot (Task 8, customer channel) — the finish
 * is the actual day+time the job wraps up, not a bare "next day" marker.
 */
export function slotButtonLabel(slot: RolloverSlotLike): string {
  const time = dayjs(slot.start_time).format('h:mm A');
  if (!slot.is_multi_day) return time;
  const finish = rolloverFinishLabel(slot);
  return finish ? `${time} → ${finish}` : `${time} → next day`;
}

/**
 * Always-visible emergency-call line rendered below the slot grids so
 * customers who need something sooner than the next available slot know to
 * call the shop directly.
 */
export function EmergencyBookingLine({ phone }: { phone?: string }) {
  return (
    <Text type="secondary">
      ☎ Need it sooner? Call for emergency booking: {phone}
    </Text>
  );
}
